/**
 * Documenti API Queries
 * Centralized data fetching for Contracts and Templates
 * Tables: contratti, contract_templates
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  Contratto, ContrattoInsert, ContrattoUpdate,
  ContrattoTemplate, ContrattoTemplateInsert, ContrattoTemplateUpdate,
  QueryOptions 
} from '@/types';
import { DatabaseTable } from '@/types/database.types';

// ============================================================
// CONTRATTI - GET Queries
// ============================================================

export async function getContratti(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(DatabaseTable.CONTRATTI)
    .select('*, properties(nome), ospiti(nome, cognome)', { count: options?.count })
    .eq('user_id', userId);

  if (options?.filters?.eq) {
    Object.entries(options.filters.eq).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  } else {
    query = query.order('created_at', { ascending: false });
  }

  if (options?.limit) query = query.limit(options.limit);
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;
  return { data: data as Contratto[] | null, error, count };
}

export async function getContrattoById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CONTRATTI)
    .select('*, properties(*), ospiti(*)', { count: options?.count })
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return { data: data as Contratto | null, error };
}

export async function getContrattiByStato(stato: Contratto['stato'], options?: QueryOptions) {
  return getContratti({
    ...options,
    filters: { 
      ...options?.filters,
      eq: { ...options?.filters?.eq, stato } 
    },
  });
}

export async function getContrattiInScadenza(giorni: number = 30) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const today = new Date();
  const future = new Date();
  future.setDate(today.getDate() + giorni);
  
  const { data, error } = await supabase
    .from(DatabaseTable.CONTRATTI)
    .select('*, properties(nome), ospiti(nome, cognome)')
    .eq('user_id', userId)
    .gte('data_fine', today.toISOString().split('T')[0])
    .lte('data_fine', future.toISOString().split('T')[0])
    .order('data_fine', { ascending: true });

  return { data: data as Contratto[] | null, error };
}

// ============================================================
// CONTRATTI - POST/PATCH/DELETE
// ============================================================

export async function createContratto(contratto: ContrattoInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CONTRATTI)
    .insert({ ...contratto, user_id: uid })
    .select()
    .single();

  return { data: data as Contratto | null, error };
}

export async function updateContratto(id: string, updates: ContrattoUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CONTRATTI)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return { data: data as Contratto | null, error };
}

export async function deleteContratto(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(DatabaseTable.CONTRATTI)
    .delete()
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// ============================================================
// CONTRATTO TEMPLATES - GET Queries
// ============================================================

export async function getContrattoTemplates(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(DatabaseTable.CONTRACT_TEMPLATES)
    .select('*', { count: options?.count })
    .eq('user_id', userId)
    .eq('attivo', true);

  if (options?.filters?.tipo) {
    query = query.eq('tipo', options.filters.tipo);
  }

  if (options?.order) {
    query = query.order(options.order.column, { ascending: options.order.ascending ?? true });
  } else {
    query = query.order('nome', { ascending: true });
  }

  if (options?.limit) query = query.limit(options.limit);

  const { data, error, count } = await query;
  return { data: data as ContrattoTemplate[] | null, error, count };
}

export async function getContrattoTemplateById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CONTRACT_TEMPLATES)
    .select('*')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return { data: data as ContrattoTemplate | null, error };
}

// ============================================================
// CONTRATTO TEMPLATES - POST/PATCH/DELETE
// ============================================================

export async function createContrattoTemplate(template: ContrattoTemplateInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CONTRACT_TEMPLATES)
    .insert({ ...template, user_id: uid })
    .select()
    .single();

  return { data: data as ContrattoTemplate | null, error };
}

export async function updateContrattoTemplate(id: string, updates: ContrattoTemplateUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CONTRACT_TEMPLATES)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return { data: data as ContrattoTemplate | null, error };
}

export async function deleteContrattoTemplate(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  // Soft delete - set attivo to false
  const { error } = await supabase
    .from(DatabaseTable.CONTRACT_TEMPLATES)
    .update({ attivo: false })
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// ============================================================
// STATS
// ============================================================

export async function getContrattiStats(userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CONTRATTI)
    .select('stato')
    .eq('user_id', uid);

  if (error) return { stats: null, error };

  const stats = {
    bozza: data?.filter(c => c.stato === 'bozza').length || 0,
    attivi: data?.filter(c => c.stato === 'attivo').length || 0,
    completati: data?.filter(c => c.stato === 'completato').length || 0,
    cancellati: data?.filter(c => c.stato === 'cancellato').length || 0,
    totali: data?.length || 0,
  };

  return { stats, error: null };
}
